import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';

if (!customElements.get('collection-banner-filter')) {
  /**
   * Turns Collection Banner List tiles that carry a filter add-on
   * (data-filter-params, e.g. "filter.p.m.custom.category=Rings") into
   * category tabs for the collection being viewed: a click applies the add-on
   * through facets-form-component.updateFiltersByURL (the theme's AJAX section
   * morph) instead of loading the tile's link.
   *
   * Tabs are single-select like the category chips: a tile replaces any other
   * value of its filter, drops values that only apply within the previous
   * category (the sub-categories), and clicking the selected tile removes its
   * filter again. When the page has no facets form (e.g. the homepage) the
   * click falls through to the plain link.
   */
  class CollectionBannerFilter extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('pointerover', this.#handlePointerOver);
      document.addEventListener(ThemeEvents.FilterUpdate, this.#syncActiveState);
      window.addEventListener('popstate', this.#syncActiveState);
      this.#syncActiveState();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#handleClick);
      this.removeEventListener('pointerover', this.#handlePointerOver);
      document.removeEventListener(ThemeEvents.FilterUpdate, this.#syncActiveState);
      window.removeEventListener('popstate', this.#syncActiveState);
    }

    /**
     * @param {MouseEvent} event
     */
    #handleClick = (event) => {
      // Leave modified clicks (new tab/window) to the browser.
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const tile = this.#tileFrom(event.target);
      if (!tile) return;

      const facetsForm = findFacetsForm();
      if (!facetsForm || typeof facetsForm.updateFiltersByURL !== 'function') return;

      event.preventDefault();

      // updateFiltersByURL pushes the history entry and dispatches the filter
      // update event, which re-syncs the selected tile.
      facetsForm.updateFiltersByURL(buildTargetURL(tile).toString());
    };

    /**
     * Warm the section cache while the pointer is over a tile, so the results
     * are usually local by the time the user clicks.
     * @param {PointerEvent} event
     */
    #handlePointerOver = (event) => {
      const tile = this.#tileFrom(event.target);
      if (!tile) return;

      const sectionId = findFacetsForm()?.getAttribute('section-id');
      if (!sectionId) return;

      sectionRenderer.getSectionHTML(sectionId, true, buildTargetURL(tile)).catch(() => {});
    };

    #syncActiveState = () => {
      const params = new URL(window.location.href).searchParams;

      for (const tile of this.querySelectorAll('a[data-filter-params]')) {
        if (isApplied(parseAddOn(tile), params)) {
          tile.setAttribute('aria-current', 'true');
        } else {
          tile.removeAttribute('aria-current');
        }
      }
    };

    /**
     * @param {EventTarget | null} target
     * @returns {HTMLAnchorElement | null}
     */
    #tileFrom(target) {
      if (!(target instanceof Element)) return null;

      const tile = target.closest('a[data-filter-params]');
      return tile instanceof HTMLAnchorElement && this.contains(tile) ? tile : null;
    }
  }

  /**
   * @param {HTMLElement} tile
   * @returns {URLSearchParams}
   */
  function parseAddOn(tile) {
    return new URLSearchParams(tile.dataset.filterParams ?? '');
  }

  /**
   * @param {URLSearchParams} addOn
   * @param {URLSearchParams} params
   */
  function isApplied(addOn, params) {
    const entries = [...addOn.entries()];
    return entries.length > 0 && entries.every(([key, value]) => params.getAll(key).includes(value));
  }

  /**
   * The current URL with the tile's add-on toggled in single-select fashion.
   * Other params (sorting, unrelated filters) are kept.
   * @param {HTMLElement} tile
   * @returns {URL}
   */
  function buildTargetURL(tile) {
    const url = new URL(window.location.href);
    const addOn = parseAddOn(tile);
    const wasApplied = isApplied(addOn, url.searchParams);
    const keys = new Set(addOn.keys());

    for (const key of keys) url.searchParams.delete(key);

    // Values scoped to a parent filter value (the sub-category hidden inputs)
    // no longer apply once that parent filter changes.
    for (const input of document.querySelectorAll('facets-form-component input[type="hidden"][data-parent-param]')) {
      if (input instanceof HTMLInputElement && keys.has(input.dataset.parentParam ?? '')) {
        url.searchParams.delete(input.name);
      }
    }

    url.searchParams.delete('page');

    if (!wasApplied) {
      for (const [key, value] of addOn) url.searchParams.append(key, value);
    }

    return url;
  }

  /**
   * The page's main facets form (the drawer copy lives inside a dialog).
   * @returns {(HTMLElement & { updateFiltersByURL?: (url: string) => void }) | null}
   */
  function findFacetsForm() {
    const candidates = document.querySelectorAll('facets-form-component[section-id]');

    for (const candidate of candidates) {
      if (!candidate.closest('dialog')) return /** @type {HTMLElement} */ (candidate);
    }

    return /** @type {HTMLElement | null} */ (candidates[0] ?? null);
  }

  customElements.define('collection-banner-filter', CollectionBannerFilter);
}
